import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import PageHeader from "../components/common/PageHeader";
import FormField from "../components/common/FormField";
import ToggleSwitch from "../components/common/ToggleSwitch";
import { useControlCash, CURRENCY_OPTIONS } from "../store/controlCashStore";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { usePersistedFormState } from "../hooks/usePersistedFormState";
import { DAYS_OF_MONTH } from "../constants";
import { getTodayDate } from "../utils/common";

// ============================================================================
// HELPERS
// ============================================================================
// Categorías = las que crea el usuario + "Ahorro" (flujo especial para aportar a metas)
const getCategories = (customCategories = []) => [
  ...(customCategories || []).map(c => ({ value: c.id, label: c.name })),
  { value: "savings", label: "Ahorro (aportar a meta)" }
];

// ============================================================================
// COMPONENTE: ExpenseForm
// ============================================================================
const ExpenseForm = ({ onSubmit, onCancel, isLoading, initialData, goals = [], categories = [], currency, formatAmount, storageKey, enabledCurrencies = ["PEN", "USD"], customCategories = [], onCreateCategory }) => {
  const firstCategory = categories.find(c => c.value !== "savings")?.value || categories[0]?.value || "";
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const toast = useToast();
  
  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      toast.warning("Escribe el nombre de la categoría");
      return;
    }
    if (customCategories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      toast.warning("Ya existe una categoría con ese nombre");
      return;
    }
    if (onCreateCategory) {
      try {
        const newCategory = await onCreateCategory(name);
        if (newCategory && newCategory.id) {
          handleChange("category", newCategory.id);
          setShowCreateCategory(false);
          setNewCategoryName("");
          toast.success(`Categoría "${name}" creada`);
        }
      } catch (error) {
        console.error("Error creando categoría:", error);
        toast.error("Error al crear la categoría");
      }
    }
  };
  
  const hasOnlySavings = categories.filter(c => c.value !== "savings").length === 0;
  const defaultState = {
    name: "",
    amount: "",
    currency: "PEN",
    category: firstCategory,
    date: getTodayDate(),
    note: "",
    isRecurring: false,
    dueDay: null,
    goalId: "",
    status: "PAID" // Por defecto "Pagado"
  };

  const [formData, setFormData, clearPersisted, wasRestored] = usePersistedFormState(storageKey, defaultState);

  // Cargar datos iniciales si existen (modo edición) y no se restauró desde storage
  useEffect(() => {
    if (wasRestored) return;
    if (initialData) {
      const amountStr = typeof initialData.amount === "number"
        ? initialData.amount.toString()
        : String(initialData.amount ?? "");
      setFormData({
        name: initialData.name,
        amount: amountStr,
        currency: initialData.currency || "PEN",
        category: initialData.category,
        date: initialData.date,
        note: initialData.note,
        isRecurring: initialData.isRecurring,
        dueDay: initialData.dueDay,
        status: initialData.status || "PAID"
      });
    } else if (categories.length > 0) {
      const defCat = categories.find(c => c.value !== "savings")?.value || categories[0]?.value;
      if (defCat) {
        setFormData(prev => {
          const isValid = categories.some(c => c.value === prev.category);
          return isValid ? prev : { ...prev, category: defCat };
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, categories, wasRestored]);

  // Estado de errores
  const [errors, setErrors] = useState({});

  // Estado de campos tocados (para mostrar errores solo después de interacción)
  const [touched, setTouched] = useState({});

  // Validación en tiempo real
  useEffect(() => {
    const newErrors = {};

    if (touched.name && !formData.name.trim()) {
      newErrors.name = "El nombre del gasto es obligatorio";
    }

    if (touched.amount) {
      if (!formData.amount) {
        newErrors.amount = "El monto es obligatorio";
      } else if (parseFloat(formData.amount) <= 0) {
        newErrors.amount = "El monto debe ser mayor a 0";
      }
    }

    if (formData.isRecurring && touched.dueDay && !formData.dueDay) {
      newErrors.dueDay = "Selecciona el día de vencimiento";
    }

    if (formData.category === "savings" && touched.goalId && !formData.goalId) {
      newErrors.goalId = "Selecciona una meta para este ahorro";
    }

    setErrors(newErrors);
  }, [formData, touched]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Marcar todos los campos como tocados
    setTouched({
      name: true,
      amount: true,
      category: true,
      date: true,
      dueDay: formData.isRecurring,
      goalId: formData.category === "savings"
    });

    // Validar todo el formulario
    const validationErrors = {};

    if (!formData.name.trim()) {
      validationErrors.name = "El nombre del gasto es obligatorio";
    }

    if (!formData.amount) {
      validationErrors.amount = "El monto es obligatorio";
    } else if (parseFloat(formData.amount) <= 0) {
      validationErrors.amount = "El monto debe ser mayor a 0";
    }

    if (formData.isRecurring && !formData.dueDay) {
      validationErrors.dueDay = "Selecciona el día de vencimiento";
    }

    if (formData.category === "savings" && !formData.goalId) {
      validationErrors.goalId = "Selecciona una meta para este ahorro";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Si no hay errores, limpiar persistencia y enviar el formulario
    clearPersisted();
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount),
      currency: formData.currency || "PEN"
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Nombre del gasto */}
      <FormField 
        label="Nombre del gasto" 
        required 
        error={touched.name && errors.name}
      >
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          onBlur={() => handleBlur("name")}
          placeholder="Ej: Netflix, Supermercado, Gasolina..."
          className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
            touched.name && errors.name
              ? "border-red-300 focus:border-red-500 focus:ring-red-200"
              : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
          }`}
          disabled={isLoading}
        />
      </FormField>

      {/* Monto y Moneda */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <FormField 
            label="Monto" 
            required 
            error={touched.amount && errors.amount}
          >
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold whitespace-nowrap">
                {CURRENCY_OPTIONS.find(opt => opt.code === formData.currency)?.symbol || "S/"}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
                onBlur={() => handleBlur("amount")}
                placeholder="0.00"
                className={`w-full pl-20 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                  touched.amount && errors.amount
                    ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                }`}
                disabled={isLoading}
              />
            </div>
          </FormField>
        </div>
        <div>
          <FormField label="Moneda" required>
            <div className="relative">
              <select
                value={formData.currency}
                onChange={(e) => handleChange("currency", e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
                disabled={isLoading}
              >
                {CURRENCY_OPTIONS.filter(opt => enabledCurrencies.includes(opt.code)).map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <svg 
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </FormField>
        </div>
      </div>

      {/* Categoría */}
      <FormField 
        label="Categoría" 
        required 
        helperText={hasOnlySavings ? "Crea una categoría para clasificar tus gastos." : undefined}
      >
        <div className="space-y-2">
          <div className="relative">
            <select
              value={formData.category}
              onChange={(e) => handleChange("category", e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
              disabled={isLoading}
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
            <svg 
              className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {/* Botón para crear categoría - Siempre visible */}
          {!showCreateCategory ? (
            <button
              type="button"
              onClick={() => setShowCreateCategory(true)}
              className="w-full px-4 py-2.5 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 text-blue-700 text-sm font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Crear categoría
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateCategory();
                  } else if (e.key === "Escape") {
                    setShowCreateCategory(false);
                    setNewCategoryName("");
                  }
                }}
                placeholder="Nombre de la categoría"
                className="flex-1 px-4 py-2.5 border-2 border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all"
                autoFocus
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={handleCreateCategory}
                disabled={isLoading || !newCategoryName.trim()}
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl transition-colors"
              >
                Crear
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateCategory(false);
                  setNewCategoryName("");
                }}
                disabled={isLoading}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors"
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      </FormField>

      {/* Selector de Meta (solo si categoría es "Ahorro") */}
      {formData.category === "savings" && (
        <FormField 
          label="¿A qué meta quieres aportar?" 
          required
          error={errors.goalId}
          helperText={goals.length === 0 ? "No tienes metas creadas. Ve a 'Metas de Ahorro' para crear una." : "Selecciona la meta a la que quieres agregar este ahorro"}
        >
          <div className="relative">
            <select
              value={formData.goalId}
              onChange={(e) => handleChange("goalId", e.target.value)}
              onBlur={() => handleBlur("goalId")}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none bg-white cursor-pointer ${
                errors.goalId
                  ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                  : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
              }`}
              disabled={isLoading || goals.length === 0}
            >
              <option value="">Selecciona una meta...</option>
              {goals
                .filter(g => g.status === "ACTIVE")
                .map((goal) => (
                  <option key={goal.id} value={goal.id}>
                    {goal.name} (Meta: {formatAmount(goal.targetAmount)})
                  </option>
                ))}
            </select>
            <svg 
              className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </FormField>
      )}

      {/* Estado (Pendiente/Pagado) */}
      <FormField label="Estado" required>
        <div className="relative">
          <select
            value={formData.status || "PAID"}
            onChange={(e) => handleChange("status", e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
            disabled={isLoading}
          >
            <option value="PAID">Pagado</option>
            <option value="PENDING">Pendiente</option>
          </select>
          <svg 
            className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </FormField>

      {/* Fecha */}
      <FormField label="Fecha" required>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => handleChange("date", e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all"
          disabled={isLoading}
        />
      </FormField>

      {/* Nota (opcional) */}
      <FormField label="Nota" helperText="Agrega detalles adicionales (opcional)">
        <textarea
          value={formData.note}
          onChange={(e) => handleChange("note", e.target.value)}
          placeholder="Ej: Pago mensual, incluye descuento..."
          rows={3}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all resize-none"
          disabled={isLoading}
        />
      </FormField>

      {/* Switch: ¿Es gasto recurrente? */}
      <div className="mb-6">
        <ToggleSwitch
          enabled={formData.isRecurring}
          onChange={(value) => {
            handleChange("isRecurring", value);
            if (!value) {
              handleChange("dueDay", null);
            }
          }}
          label="¿Es gasto recurrente?"
          description="Se repetirá automáticamente cada mes"
        />
      </div>

      {/* Día de vencimiento (condicional) */}
      {formData.isRecurring && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800 mb-1">Gasto recurrente</h4>
              <p className="text-sm text-gray-600 mb-4">
                Este gasto se repetirá todos los meses en el día que selecciones
              </p>
              
              <FormField 
                label="Día de vencimiento" 
                required 
                error={touched.dueDay && errors.dueDay}
              >
                <div className="relative">
                  <select
                    value={formData.dueDay || ""}
                    onChange={(e) => handleChange("dueDay", e.target.value ? parseInt(e.target.value) : null)}
                    onBlur={() => handleBlur("dueDay")}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none bg-white cursor-pointer ${
                      touched.dueDay && errors.dueDay
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                    }`}
                    disabled={isLoading}
                  >
                    <option value="">Selecciona un día</option>
                    {DAYS_OF_MONTH.map((day) => (
                      <option key={day} value={day}>
                        Día {day}
                      </option>
                    ))}
                  </select>
                  <svg 
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </FormField>
            </div>
          </div>
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t-2 border-gray-100">
        <button
          type="button"
          onClick={() => { clearPersisted(); onCancel(); }}
          disabled={isLoading}
          className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Cancelar y volver"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          aria-label="Guardar gasto"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Guardando...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Guardar gasto
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL: AddEditExpense
// ============================================================================
const AddEditExpense = ({ isEditMode = false }) => {
  const { id } = useParams(); // Para modo edición
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state, dispatch, getExpenseById, formatDisplayAmount, getMonthTotalsByCurrency } = useControlCash();
  const toast = useToast();
  const { confirm } = useConfirm();
  
  const [isLoading, setIsLoading] = useState(false);
  const [monthId, setMonthId] = useState(null);
  const [initialData, setInitialData] = useState(null);

  // Obtener monthId y datos iniciales
  useEffect(() => {
    if (isEditMode && id) {
      // Modo edición: cargar expense existente
      const expense = getExpenseById(id);
      if (expense) {
        setMonthId(expense.monthId);
        setInitialData({
          name: expense.name,
          amount: expense.amount.toString(),
          currency: expense.currency || "PEN",
          category: expense.category,
          date: expense.date || getTodayDate(),
          note: expense.note || "",
          isRecurring: expense.isFromRecurring || false,
          dueDay: expense.dueDay || null,
          status: expense.status || "PAID"
        });
      }
    } else {
      // Modo creación: obtener monthId de query params
      const monthIdParam = searchParams.get("monthId");
      setMonthId(monthIdParam);
    }
  }, [id, isEditMode, searchParams, getExpenseById]);

  const handleSubmit = async (expenseData) => {
    setIsLoading(true);

    try {
      // Si es categoría "Ahorro", crear una contribución en lugar de un gasto
      if (expenseData.category === "savings" && expenseData.goalId) {
        await dispatch({
          type: "ADD_CONTRIBUTION",
          payload: {
            goalId: expenseData.goalId,
            amount: expenseData.amount,
            currency: expenseData.currency || "PEN",
            date: expenseData.date,
            note: expenseData.note || `Aporte desde ${expenseData.name}`,
            monthId: monthId
          }
        });
        console.log("💰 Contribución creada a meta:", expenseData.goalId);
        
        setIsLoading(false);
        toast.success(`Ahorro registrado exitosamente!\n\nSe agregó ${formatDisplayAmount(expenseData.amount, false, expenseData.currency)} a tu meta de ahorro.`);
        
        if (monthId) {
          navigate(`/month/${monthId}`);
        } else {
          navigate("/");
        }
        return;
      }

      // Flujo normal para gastos
      if (isEditMode && id) {
        await dispatch({
          type: "UPDATE_EXPENSE",
          payload: {
            id,
            patch: {
              name: expenseData.name,
              amount: expenseData.amount,
              currency: expenseData.currency || "PEN",
              category: expenseData.category,
              date: expenseData.date,
              note: expenseData.note,
              dueDay: expenseData.isRecurring ? expenseData.dueDay : null,
              status: expenseData.status || "PAID"
            }
          }
        });
        console.log("✏️ Gasto actualizado:", id);
        setIsLoading(false);
        toast.success(`Gasto actualizado exitosamente!`);
        
        if (monthId) {
          navigate(`/month/${monthId}`);
        } else {
          navigate("/");
        }
        return;
      }

      // Para gastos nuevos, verificar balance antes de crear
      const expenseCurrency = expenseData.currency || "PEN";
      const expenseAmount = parseFloat(expenseData.amount) || 0;
      
      // Solo verificar si el gasto se marca como pagado y hay un monthId
      if (monthId && expenseAmount > 0) {
        const totalsByCurrency = getMonthTotalsByCurrency(monthId);
        const currencyTotals = totalsByCurrency[expenseCurrency];
        
        if (currencyTotals) {
          // Calcular el balance disponible después de agregar este gasto
          const currentBalance = currencyTotals.availableRemaining;
          const balanceAfterExpense = currentBalance - expenseAmount;
          
          // Si el balance quedaría negativo, ofrecer conversión
          if (balanceAfterExpense < 0) {
            // Buscar otras monedas con balance positivo
            const availableCurrencies = Object.keys(totalsByCurrency)
              .filter(curr => {
                const totals = totalsByCurrency[curr];
                return curr !== expenseCurrency && totals.availableRemaining > 0;
              })
              .map(curr => {
                const totals = totalsByCurrency[curr];
                const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === curr);
                return {
                  code: curr,
                  label: currencyInfo?.label || curr,
                  symbol: currencyInfo?.symbol || curr,
                  available: totals.availableRemaining
                };
              });

            if (availableCurrencies.length > 0) {
              // Calcular cuánto se necesita convertir desde cada moneda disponible
              const exchangeRates = state.exchangeRates || {};
              const conversionOptions = availableCurrencies.map(fromCurr => {
                const fromRate = exchangeRates[fromCurr.code] || 1;
                const toRate = exchangeRates[expenseCurrency] || 1;
                const exchangeRate = toRate / fromRate;
                // Calcular cuánto necesitamos en la moneda origen para obtener el monto del gasto
                const neededInFromCurrency = expenseAmount / exchangeRate;
                
                return {
                  ...fromCurr,
                  exchangeRate,
                  neededAmount: neededInFromCurrency,
                  canAfford: fromCurr.available >= neededInFromCurrency
                };
              });

              // Usar la primera moneda que tenga suficiente balance, o la primera disponible
              const selectedOption = conversionOptions.find(opt => opt.canAfford) || conversionOptions[0];
              
              const expenseCurrencyLabel = expenseCurrency === "PEN" ? "soles" : expenseCurrency === "USD" ? "dólares" : expenseCurrency;
              const fromCurrencyLabel = selectedOption.label;
              
              const shouldConvert = await confirm({
                title: "Fondos insuficientes",
                message: `No tienes suficiente balance en ${expenseCurrencyLabel} para pagar este gasto.\n\n` +
                  `Balance disponible: ${formatDisplayAmount(currentBalance, false, expenseCurrency)}\n` +
                  `Gasto a pagar: ${formatDisplayAmount(expenseAmount, false, expenseCurrency)}\n` +
                  `Faltan: ${formatDisplayAmount(Math.abs(balanceAfterExpense), false, expenseCurrency)}\n\n` +
                  `¿Quieres convertir ${formatDisplayAmount(selectedOption.neededAmount, false, selectedOption.code)} desde ${fromCurrencyLabel} para pagar este gasto?\n\n` +
                  `Tipo de cambio: 1 ${selectedOption.code} = ${selectedOption.exchangeRate.toFixed(4)} ${expenseCurrency}`,
                confirmLabel: "Sí, convertir y pagar",
                cancelLabel: "Cancelar"
              });

              if (!shouldConvert) {
                // Usuario canceló, no crear el gasto
                setIsLoading(false);
                return;
              }

              // Crear conversión automática antes del gasto
              try {
                await dispatch({
                  type: "ADD_CURRENCY_CONVERSION",
                  payload: {
                    monthId: monthId,
                    fromCurrency: selectedOption.code,
                    fromAmount: selectedOption.neededAmount,
                    toCurrency: expenseCurrency,
                    toAmount: expenseAmount,
                    exchangeRate: selectedOption.exchangeRate,
                    date: expenseData.date || new Date().toISOString().split("T")[0],
                    note: `Conversión automática para pagar: ${expenseData.name}`
                  }
                });
              } catch (error) {
                console.error("Error al crear conversión:", error);
                toast.error("Error al crear la conversión. Intenta de nuevo.");
                setIsLoading(false);
                return;
              }
            }
          }
        }
      }

      // Crear el gasto
      const expenseStatus = expenseData.status || "PAID";
      await dispatch({
        type: "ADD_EXPENSE",
        payload: {
          monthId,
          name: expenseData.name,
          amount: expenseData.amount,
          currency: expenseData.currency || "PEN",
          category: expenseData.category,
          date: expenseData.date,
          note: expenseData.note,
          status: expenseStatus,
          dueDay: expenseData.isRecurring ? expenseData.dueDay : null,
          isFromRecurring: expenseData.isRecurring,
          recurringTemplateId: null,
          paidAt: expenseStatus === "PAID" ? new Date().toISOString() : null
        }
      });
      console.log("➕ Gasto creado");

      setIsLoading(false);
      toast.success(`Gasto guardado exitosamente!`);
      
      // Navegar de vuelta al detalle del mes
      if (monthId) {
        navigate(`/month/${monthId}`);
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Error al guardar gasto:", error);
      setIsLoading(false);
      toast.error("Error al guardar el gasto. Intenta de nuevo.");
    }
  };

  const handleCancel = () => {
    if (monthId) {
      navigate(`/month/${monthId}`);
    } else {
      navigate("/");
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <PageHeader 
          isEditMode={isEditMode} 
          onBack={handleCancel}
          backAriaLabel="Volver a detalle del mes"
        />

        {/* Card principal del formulario */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <ExpenseForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
            initialData={initialData}
            goals={state.goals}
            categories={getCategories(state.customCategories)}
            currency={state.currencySymbol ?? state.currency}
            formatAmount={(amount) => formatDisplayAmount(amount, false, "PEN")}
            enabledCurrencies={state.enabledCurrencies || ["PEN", "USD"]}
            customCategories={state.customCategories || []}
            onCreateCategory={async (name) => {
              try {
                const newCategory = await dispatch({ type: "CREATE_CUSTOM_CATEGORY", payload: { name } });
                return newCategory;
              } catch (error) {
                console.error("Error creando categoría:", error);
                return null;
              }
            }}
            storageKey={isEditMode && id ? `expense_edit_${id}` : `expense_add_${monthId || "new"}`}
          />
        </div>

        {/* Sticky button en mobile (opcional, para mejor UX) */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t-2 border-gray-200 shadow-lg z-10">
          <button
            onClick={() => document.querySelector('form').requestSubmit()}
            disabled={isLoading}
            className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
            aria-label="Guardar gasto"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Guardando...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardar gasto
              </>
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default AddEditExpense;
